import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { taskKeys } from '@/lib/queryKeys';
import { taskEndpoints } from '@/lib/api/endpoints';
import { apiDelete, operationsClient } from '@/lib/api';
import type { TaskListResponse } from '@/types/task';

export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useApiMutation<void, string>({
    mutationFn: (id) => apiDelete<void>(operationsClient, taskEndpoints.delete(id)),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: taskKeys.lists() });
      const snapshots = new Map<string, TaskListResponse>();
      queryClient
        .getQueriesData<TaskListResponse>({ queryKey: taskKeys.lists() })
        .forEach(([key, data]) => {
          if (data) {
            snapshots.set(JSON.stringify(key), data);
            queryClient.setQueryData<TaskListResponse>(key as string[], {
              ...data,
              tasks: data.tasks.filter((t) => t.id !== id),
              total: data.total - 1,
            });
          }
        });
      return { snapshots };
    },
    onError: (_err, id, context) => {
      const ctx = context as { snapshots?: Map<string, TaskListResponse> } | undefined;
      ctx?.snapshots?.forEach((data, keyStr) => {
        queryClient.setQueryData(JSON.parse(keyStr) as string[], data);
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: taskKeys.countsAll() });
      toast.success('Task deleted');
    },
  });
}
